/* eslint-disable import/no-named-as-default */
'use client'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form'
import { Select, SelectTrigger, SelectValue } from '@components/ui/select'
import {
  SelectCountryContent,
  zodCountryOptions,
} from '@components/form/select-country'
import {
  SelectIndustryContent,
  zodIndustryOptions,
} from '@components/form/select-industry'
import { type ContactResponse } from '@app/api/contact/route'
import { type ContactRequest } from '@lib/types'

import FormTermsAndConditions from '@components/form/terms-and-conditions'
import { Input } from '@components/ui/input'
import { Switch } from '@components/ui/switch'
import { Toaster } from '@components/ui/sonner'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { useUtmInitialValues } from '@components/form/utm-fields'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from '@components/interaction/button'
import { useState } from 'react'
import { useSearchParams } from 'next/navigation'
import logger from '@config/logger'
import { executeRecaptcha } from '@lib/recaptcha-client'
import { Textarea } from '@components/ui/textarea'
import { MailCheck } from 'lucide-react'
import Recaptcha from '@components/form/recaptcha'
import { sendGTMEvent } from '@next/third-parties/google'
import { FormEvent, type PosthogPerson, StandardEvent } from '@lib/analytics'
import posthog from 'posthog-js'

const formSchema = z.object({
  firstName: z.string().min(2, 'First name is required').max(50),
  lastName: z.string().min(2, 'Last name is required').max(50),
  email: z.string().email(),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().min(2, 'Job title is required').max(50),
  company: z.string().optional(),
  country: zodCountryOptions,
  industry: zodIndustryOptions,
  message: z.string().optional(),
  subscribe: z.boolean(),
})

interface ContactFormProps {
  showHeader?: boolean
}

export default function ContactForm({ showHeader = true }: ContactFormProps) {
  const utmInitialValues = useUtmInitialValues()
  const [loadRecaptcha, setLoadRecaptcha] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [contactSucceeded, setContactSucceeded] = useState(false)
  const searchParams = useSearchParams()
  const debug = searchParams.get('debug')

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      jobTitle: '',
      company: '',
      country: '',
      industry: '',
      message: '',
      subscribe: true,
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setSubmitting(true)

    try {
      const token = await executeRecaptcha(FormEvent.Contact)

      if (!token) {
        toast.error('Failed to Send', {
          description: 'We could not verify that you are not a bot.',
          action: {
            label: 'Close',
            onClick: () => null,
          },
        })
        setSubmitting(false)
        return
      }

      const { message, ...rest } = values
      const person: PosthogPerson = rest
      posthog.identify(values.email, person)

      const data: ContactRequest = {
        ...utmInitialValues,
        ...values,
        token,
      }

      if (debug === 'true') {
        toast('Debug: ContactRequest', {
          description: (
            <pre className='mt-2 max-w-80 overflow-x-scroll rounded-md bg-slate-950 p-4'>
              <code className='text-white'>
                {JSON.stringify(data, null, 2)}
              </code>
            </pre>
          ),
        })
      }

      await postContactForm(data)
      posthog.capture(StandardEvent.GenerateLead, {
        channel: FormEvent.Contact,
        industry: data.industry,
        subscribe: data.subscribe,
        ...utmInitialValues,
      })
      sendGTMEvent({ event: StandardEvent.GenerateLead })
      setContactSucceeded(true)
    } catch (error) {
      if (debug === 'true') {
        logger.error(error)
      }
      toast.error('Failed to Send', {
        description: 'Something went wrong. Please try again.',
        action: {
          label: 'Close',
          onClick: () => null,
        },
      })
    }
    setSubmitting(false)
  }

  return (
    <Form {...form}>
      {loadRecaptcha && <Recaptcha />}
      <form
        id={FormEvent.Contact}
        onSubmit={form.handleSubmit(onSubmit)}
        className='mx-auto h-fit bg-white p-5 shadow-md sm:p-8 md:max-w-4xl lg:p-10 xl:p-12'
      >
        {showHeader && (
          <h2 className='mb-5 text-h8 font-bold sm:text-h6'>Get In Touch</h2>
        )}
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-5'>
          <FormField
            control={form.control}
            name='firstName'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoComplete='given-name'
                    placeholder='First name *'
                    onFocus={() => {
                      setLoadRecaptcha(true)
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='lastName'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoComplete='family-name'
                    placeholder='Last name *'
                    onFocus={() => {
                      setLoadRecaptcha(true)
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoComplete='email'
                    placeholder='Email *'
                    onFocus={() => {
                      setLoadRecaptcha(true)
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='phoneNumber'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoComplete='tel'
                    placeholder='Phone number'
                    onFocus={() => {
                      setLoadRecaptcha(true)
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='jobTitle'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoComplete='organization-title'
                    placeholder='Job title *'
                    onFocus={() => {
                      setLoadRecaptcha(true)
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='company'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    autoComplete='organization'
                    placeholder='Company name'
                    onFocus={() => {
                      setLoadRecaptcha(true)
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='country'
            render={({ field }) => (
              <FormItem>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder='Country *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectCountryContent />
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='industry'
            render={({ field }) => (
              <FormItem>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder='Industry *'
                        onFocus={() => {
                          setLoadRecaptcha(true)
                        }}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectIndustryContent />
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name='message'
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder='Message'
                  className='mt-5 resize-none'
                  rows={6}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='subscribe'
          render={({ field }) => (
            <FormItem className='mx-2 my-6 flex flex-row items-center space-x-3'>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onFocus={() => {
                    setLoadRecaptcha(true)
                  }}
                />
              </FormControl>
              <FormLabel>
                I would like to receive marketing communications regarding V2
                insights, services and events.
              </FormLabel>
            </FormItem>
          )}
        />
        <Button
          disabled={submitting || contactSucceeded}
          variant='contained'
          size='md'
          loading={submitting}
          colour='secondary'
          type='submit'
        >
          {contactSucceeded ? (
            <div className='flex items-center'>
              <span className='mr-2'>Submitted</span> <MailCheck />
            </div>
          ) : submitting ? (
            'Submitting...'
          ) : (
            'Contact'
          )}
        </Button>
        <FormTermsAndConditions submitButtonLabel='Contact' />
      </form>
      <Toaster />
    </Form>
  )
}

const postContactForm = async (data: ContactRequest): Promise<void> => {
  const response = await fetch('/api/contact', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  const responseData: ContactResponse = await response.json()

  if (response.ok) {
    toast('Contact request submitted', {
      description: responseData.message,
      action: {
        label: 'Close',
        onClick: () => null,
      },
    })
  } else {
    throw new Error(responseData.message)
  }
}
